'use client';

import { useEffect, useState } from 'react';
import {
  acceptedVideoMimeTypes,
  isVideoFile,
  readVideoMetadata,
  type VideoMetadata,
} from '@/lib/browser-video-tools';

export type LoadedVideoSource = {
  file: File;
  previewUrl: string;
  metadata: VideoMetadata;
};

export type LoadedVideoItem = LoadedVideoSource & {
  id: string;
};

export function formatVideoDurationMs(durationMs: number) {
  const totalSeconds = Math.max(0, Math.round(durationMs / 10) / 100);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  if (minutes > 0) {
    return `${minutes}:${seconds.toFixed(2).padStart(5, '0')}`;
  }

  return `${seconds.toFixed(2)}s`;
}

export function useLoadedVideoSource() {
  const [source, setSource] = useState<LoadedVideoSource | null>(null);

  useEffect(() => {
    return () => {
      if (source?.previewUrl) {
        URL.revokeObjectURL(source.previewUrl);
      }
    };
  }, [source]);

  async function loadSource(file: File) {
    if (!isVideoFile(file)) {
      throw new Error('Choose one video file for this tool.');
    }

    const metadata = await readVideoMetadata(file);

    setSource((current) => {
      if (current?.previewUrl) {
        URL.revokeObjectURL(current.previewUrl);
      }

      return {
        file,
        metadata,
        previewUrl: URL.createObjectURL(file),
      };
    });
  }

  function clearSource() {
    setSource((current) => {
      if (current?.previewUrl) {
        URL.revokeObjectURL(current.previewUrl);
      }

      return null;
    });
  }

  return {
    source,
    loadSource,
    clearSource,
  };
}

export async function loadVideoQueue(files: FileList | File[] | null) {
  if (!files?.length) {
    return [] as LoadedVideoItem[];
  }

  const loaded = await Promise.all(
    Array.from(files).map(async (file, index) => {
      if (!isVideoFile(file)) {
        throw new Error('Choose video files only for this tool.');
      }

      const metadata = await readVideoMetadata(file);

      return {
        id: `${file.name}-${file.size}-${index}`,
        file,
        metadata,
        previewUrl: URL.createObjectURL(file),
      };
    }),
  );

  return loaded;
}

export function releaseVideoQueue(items: LoadedVideoItem[]) {
  items.forEach((item) => {
    if (item.previewUrl) {
      URL.revokeObjectURL(item.previewUrl);
    }
  });
}

export { acceptedVideoMimeTypes };
